import { z } from 'zod';

/** Default days between needs-survey re-admins when unset on stored client config. */
export const ISC_DEFAULT_NEEDS_SURVEY_READMIN_DAYS = 90;

/** Default days of inactivity before lapse when unset on stored client config. */
export const ISC_DEFAULT_ACTIVITY_LAPSE_DAYS = 45;

/**
 * Avon PHQ/GAD form id list. Accepts a single string (common ops / SQL mistake)
 * or a string array; always normalizes to `string[]` when present.
 */
const AvonFormIdListSchema = z.preprocess(value => {
  if (value === null) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value.length > 0 ? [value] : [];
  }
  return value;
}, z.array(z.string()).optional());

/**
 * Client ISC program config object shape (unknown keys stripped by default).
 * Payer / form ids stay placeholders until Availity / Avon PRs land.
 *
 * Day thresholds are optional in the wire schema (no Zod `.default()`): request
 * validation must not invent values that would overwrite ops config on PATCH.
 * Apply {@link withIscProgramConfigDefaults} when reading for jobs / gates.
 */
const IscProgramConfigObjectSchema = z.object({
  availityPayerIds: z.array(z.string()).optional(),
  needsSurveyKey: z.string().optional(),
  needsSurveyId: z.number().int().positive().optional(),
  avonPhqFormIds: AvonFormIdListSchema,
  avonGadFormIds: AvonFormIdListSchema,
  needsSurveyReAdminDays: z.number().int().positive().optional(),
  activityLapseDays: z.number().int().positive().optional(),
});

/**
 * Wire / PATCH validation for ISC program config — rejects unknown keys.
 */
export const IscProgramConfigSchema = IscProgramConfigObjectSchema.strict();

/**
 * Read path for stored `iscProgramConfig` JSON.
 * Strips unknown keys so known fields (e.g. Avon form ids) still apply when
 * ops/SQL added forward-compatible keys that would fail {@link IscProgramConfigSchema}.
 */
export const IscProgramConfigReadSchema = IscProgramConfigObjectSchema;

/** Inferred ISC program config type. */
export type IscProgramConfig = z.infer<typeof IscProgramConfigSchema>;

/**
 * Merges a PATCH fragment into existing ISC program config.
 * `null` clears the column; omitted keys in the patch are preserved from existing.
 *
 * @param existing - Current stored config (or null/undefined)
 * @param patch - Validated PATCH body (`null` clears)
 * @returns Merged config, or null when cleared
 */
export function mergeIscProgramConfig(
  existing: IscProgramConfig | Record<string, unknown> | null | undefined,
  patch: IscProgramConfig | null
): IscProgramConfig | null {
  if (patch === null) {
    return null;
  }
  return {
    ...(existing as IscProgramConfig | undefined),
    ...patch,
  };
}

/**
 * Applies read-time defaults for day thresholds without mutating stored JSON.
 *
 * @param config - Stored or partial config
 * @returns Config with needsSurveyReAdminDays / activityLapseDays always set
 */
export function withIscProgramConfigDefaults(
  config: IscProgramConfig | null | undefined
): IscProgramConfig & {
  needsSurveyReAdminDays: number;
  activityLapseDays: number;
} {
  return {
    ...config,
    needsSurveyReAdminDays:
      config?.needsSurveyReAdminDays ?? ISC_DEFAULT_NEEDS_SURVEY_READMIN_DAYS,
    activityLapseDays:
      config?.activityLapseDays ?? ISC_DEFAULT_ACTIVITY_LAPSE_DAYS,
  };
}

/** Hard blockers for the engage gate. */
export type IscBlockingReason =
  | 'isc_ineligible'
  | 'predetermination_missing'
  | 'needs_survey_required'
  | 'phq_or_gad_incomplete'
  | 'needs_reenrollment'
  | 'required_consents_incomplete';

/** Soft warnings (never hard-block engage under Approach A). */
export type IscWarningReason = 'needs_survey_overdue';

/**
 * One Availity predetermination service line denormalized onto enrollment status.
 * Stored on the projection entity; not returned by the PR 1 gate GET.
 */
export const IscEnrollmentServiceLineSchema = z
  .object({
    procedureCode: z.string(),
    procedure: z.string(),
    amount: z.string(),
    estimatedPatientResponsibility: z.string(),
    allowedAmount: z.string(),
    coPay: z.string(),
  })
  .strict();

/** Inferred service-line type. */
export type IscEnrollmentServiceLine = z.infer<
  typeof IscEnrollmentServiceLineSchema
>;

/**
 * PR 1 GET response: identity + computed engage gate only.
 * Full projection fields stay on the entity for writers; checklist UI expands later.
 */
export interface IscEnrollmentStatusDto {
  memberId: string;
  clientId: number | null;
  iscProgramEnabled: boolean;
  canEngage: boolean;
  blockingReasons: IscBlockingReason[];
  warnings: IscWarningReason[];
}
